import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import confetti from "canvas-confetti";
import { 
  Calendar, MapPin, Users, CreditCard, Building2, 
  Phone, Mail, FileText, Clock, CheckCircle, XCircle, AlertCircle,
  Plane, Hotel, Car, ArrowLeft, User, Briefcase, ListOrdered, Moon,
  Download, Share2, MessageSquare, Edit3, Save, Calculator, Percent, ToggleLeft,
  FileDown, FileType, ChevronDown, ScrollText, RotateCcw, Send, MessageCircle, Plus, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { addLexendFont } from "../utils/lexendFont";
import { generateQuotePdfDoc } from "../utils/quotePdf";
import { useAuth } from "../context/AuthContext";
import DashboardLayout from "../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${amount.toLocaleString('en-IN')}`;
};

const calculateNights = (travelDate, returnDate) => {
  if (!travelDate || !returnDate) return 0;
  const start = new Date(travelDate);
  const end = new Date(returnDate);
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
};

const toIsoDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().split('T')[0];
};

const calcHotelNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const diff = Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
};

const addNightsToDate = (dateStr, nights) => {
  if (!dateStr || !nights) return "";
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Number(nights));
  return d.toISOString().split('T')[0];
};

// Parse itinerary day date - handles ISO ("2026-06-01") and human-readable ("1 Jun 2026") formats
// Falls back to calculating from travel_date + day index
const parseDayDate = (dayDateStr, travelDate, dayIndex) => {
  if (dayDateStr) {
    // Try ISO format first
    const isoDate = new Date(dayDateStr + 'T00:00:00');
    if (!isNaN(isoDate.getTime())) return isoDate;
    // Try parsing as-is (handles "1 Jun 2026" etc)
    const parsed = new Date(dayDateStr);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  // Fallback: calculate from travel_date + index
  if (travelDate) {
    const base = new Date(travelDate + 'T00:00:00');
    if (!isNaN(base.getTime())) {
      const d = new Date(base);
      d.setDate(d.getDate() + dayIndex);
      return d;
    }
  }
  return null;
};

const formatDayDateLabel = (date) => {
  if (!date) return '';
  const dayAbbr = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const dayOfMonth = String(date.getDate()).padStart(2, '0');
  const monthNum = String(date.getMonth() + 1).padStart(2, '0');
  return `${dayAbbr} - ${dayOfMonth}/${monthNum}/${date.getFullYear()}`;
};

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditingGuest, setIsEditingGuest] = useState(false);
  const [guestForm, setGuestForm] = useState({
    client_name: "",
    client_phone: "",
    client_email: ""
  });

  // Agent Markup State
  const [markupMode, setMarkupMode] = useState("manual");
  const [markupPercentage, setMarkupPercentage] = useState(0);
  const [agentMarkup, setAgentMarkup] = useState({
    adult: 0,
    cwb: 0,
    cnb: 0
  });
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [addons, setAddons] = useState({ flight: 0, hotel: 0, visa: 0 });
  const [savingAddons, setSavingAddons] = useState(false);
  const [sharingWhatsApp, setSharingWhatsApp] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [gstRequired, setGstRequired] = useState(false);
  const [gstinValue, setGstinValue] = useState("");

  // Flight Details State
  const [showFlightDetails, setShowFlightDetails] = useState(false);
  const [isEditingFlight, setIsEditingFlight] = useState(false);
  // Hotel Details State
  const [showHotelDetails, setShowHotelDetails] = useState(false);
  const [isEditingHotel, setIsEditingHotel] = useState(false);
  const emptyHotelEntry = {
    city: "", hotel_name: "", room_type: "", hotel_category: "4 Star", meal_plan: "CP",
    rooms_count: 1, check_in: "", check_out: "", nights: 0,
    extra_bed_toggle: false, extra_bed_count: 0,
    cwb_toggle: false, cwb_count: 0,
    cnb_toggle: false, cnb_count: 0
  };
  const [hotelEntries, setHotelEntries] = useState([{ ...emptyHotelEntry }]);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showConfirmOptionModal, setShowConfirmOptionModal] = useState(false);
  const [confirmSelectedOption, setConfirmSelectedOption] = useState(null);
  const [confirmingBooking, setConfirmingBooking] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState("");
  const [revisionCategories, setRevisionCategories] = useState([]);
  const [requestingRevision, setRequestingRevision] = useState(false);
  const emptyFlight = {
    airline: "", flight_number: "", pnr: "", connection_type: "non_stop",
    departure_city: "", departure_code: "", departure_time: "", departure_date: "", departure_airport: "",
    arrival_city: "", arrival_code: "", arrival_time: "", arrival_date: "", arrival_airport: "",
    duration: "", stops: "", stop_type: "", stop_location: ""
  };
  const [flights, setFlights] = useState([{ ...emptyFlight }]);

  useEffect(() => {
    fetchQuoteDetail();
  }, [id]);

  const fetchQuoteDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setQuote(data);
      setGuestForm({
        client_name: data.client_name || "",
        client_phone: data.client_phone || "",
        client_email: data.client_email || ""
      });
      // Load GST preference
      setGstRequired(data.gst_required || false);
      setGstinValue(data.gstin || "");
      // Load saved markup
      if (data.markup_mode) setMarkupMode(data.markup_mode);
      if (data.markup_percentage) setMarkupPercentage(data.markup_percentage);
      setAgentMarkup({
        adult: data.markup_per_adult || 0,
        cwb: data.markup_per_cwb || 0,
        cnb: data.markup_per_cnb || 0
      });
      setAddons({
        flight: data.flight_price || 0,
        hotel: data.hotel_price || 0,
        visa: data.visa_price || 0,
      });
      // Load selected option
      if (data.selected_option !== undefined && data.selected_option !== null) {
        setSelectedOption(data.selected_option);
      }
      // Load flight details
      if (data.show_flight_details !== undefined) setShowFlightDetails(data.show_flight_details);
      // Load hotel details
      if (data.show_hotel_details !== undefined) setShowHotelDetails(data.show_hotel_details);
      if (data.flight_details) {
        // Backward-compatible: convert old single-object to array
        if (Array.isArray(data.flight_details)) {
          setFlights(data.flight_details.map(f => ({ ...emptyFlight, ...f })));
        } else if (typeof data.flight_details === 'object') {
          setFlights([{ ...emptyFlight, ...data.flight_details }]);
        }
      }
      // Load hotel details
      if (Array.isArray(data.hotels) && data.hotels.length > 0) {
        setHotelEntries(data.hotels.map(h => ({ ...emptyHotelEntry, ...h })));
      } else {
        setHotelEntries([{ ...emptyHotelEntry, check_in: toIsoDate(data.travel_date) }]);
      }
    } catch (err) {
      console.error("Error fetching quote:", err);
      setError(err.response?.data?.detail || "Failed to load quote details");
    } finally {
      setLoading(false);
    }
  };

  // Accommodation Options State
  const [selectedOption, setSelectedOption] = useState(quote?.selected_option ?? 0);
  const [selectingOption, setSelectingOption] = useState(false);
  
  const accommodationOptions = quote?.accommodation_options || [];
  const hasMultipleOptions = accommodationOptions.length > 1;
  
  // Get active option pricing (selected or primary)
  const getActiveOption = () => {
    if (accommodationOptions.length > 0 && accommodationOptions[selectedOption]) {
      return accommodationOptions[selectedOption];
    }
    return {
      quote_per_adult: quote?.quote_per_adult || 0,
      quote_per_cwb: quote?.quote_per_cwb || 0,
      quote_per_cnb: quote?.quote_per_cnb || 0,
      total_amount: quote?.quote_amount || 0,
      hotels: quote?.hotels || []
    };
  };

  const handleSelectOption = async (idx) => {
    setSelectedOption(idx);
    const opt = accommodationOptions[idx];
    if (!opt) return;
    try {
      setSelectingOption(true);
      await axios.put(`${API}/quotes/${id}/agent-update`, {
        selected_option: idx
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${opt.label || `Option ${idx + 1}`} selected!`);
      fetchQuoteDetail();
    } catch (err) {
      toast.error("Failed to select option");
    } finally {
      setSelectingOption(false);
    }
  };

  const handleSaveMarkup = async () => {
    try {
      setSavingMarkup(true);
      const payload = {
        markup_mode: markupMode,
        markup_percentage: markupMode === "percentage" ? markupPercentage : 0,
        markup_per_adult: markupMode === "manual" ? agentMarkup.adult : 0,
        markup_per_cwb: markupMode === "manual" ? agentMarkup.cwb : 0,
        markup_per_cnb: markupMode === "manual" ? agentMarkup.cnb : 0
      };
      await axios.put(`${API}/quotes/${id}/agent-update`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Markup saved successfully!");
      fetchQuoteDetail();
    } catch (err) {
      toast.error("Failed to save markup");
    } finally {
      setSavingMarkup(false);
    }
  };

  const handleSaveAddons = async () => {
    try {
      setSavingAddons(true);
      await axios.put(`${API}/quotes/${id}/agent-update`, {
        flight_price: parseFloat(addons.flight) || 0,
        hotel_price: parseFloat(addons.hotel) || 0,
        visa_price: parseFloat(addons.visa) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Add-on prices saved");
      fetchQuoteDetail();
    } catch (err) {
      toast.error("Failed to save add-ons");
    } finally {
      setSavingAddons(false);
    }
  };

  const handleSaveGuestDetails = async () => {
    try {
      await axios.put(`${API}/quotes/${id}/agent-update`, {
        ...guestForm,
        gst_required: gstRequired,
        gstin: gstinValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Guest details saved!");
      setIsEditingGuest(false);
      fetchQuoteDetail();
    } catch (err) {
      toast.error("Failed to save guest details");
    }
  };

  // Flight details handlers
  const handleToggleFlightDetails = async (val) => {
    setShowFlightDetails(val);
    try {
      await axios.put(`${API}/quotes/${id}/agent-update`, { show_flight_details: val }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {}
  };

  // Hotel details handlers
  const handleToggleHotelDetails = async (val) => {
    setShowHotelDetails(val);
    try {
      await axios.put(`${API}/quotes/${id}/agent-update`, { show_hotel_details: val }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {}
  };

  const handleSaveFlightDetails = async () => {
    try {
      await axios.put(`${API}/quotes/${id}/agent-update`, {
        flight_details: flights,
        show_flight_details: showFlightDetails
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Flight details saved!");
      setIsEditingFlight(false);
      fetchQuoteDetail();
    } catch {
      toast.error("Failed to save flight details");
    }
  };

  const updateFlight = (idx, field, val) => {
    setFlights(prev => prev.map((f, i) => {
      if (i !== idx) return f;
      const updated = { ...f, [field]: val };
      // Auto-calculate duration from departure/arrival times
      if (field === 'departure_time' || field === 'arrival_time') {
        const depStr = field === 'departure_time' ? val : updated.departure_time;
        const arrStr = field === 'arrival_time' ? val : updated.arrival_time;
        if (depStr && arrStr) {
          const parseTime = (t) => {
            const cleaned = t.replace(/\s*(hrs|hr)\s*/i, '').trim();
            const parts = cleaned.split(':');
            if (parts.length >= 2) return { h: parseInt(parts[0]) || 0, m: parseInt(parts[1]) || 0 };
            return null;
          };
          const dep = parseTime(depStr);
          const arr = parseTime(arrStr);
          if (dep && arr) {
            let diffMin = (arr.h * 60 + arr.m) - (dep.h * 60 + dep.m);
            if (diffMin < 0) diffMin += 24 * 60; // next day
            const hours = Math.floor(diffMin / 60);
            const mins = diffMin % 60;
            updated.duration = mins > 0 ? `${hours}h ${mins}m` : `${hours}h 00m`;
          }
        }
      }
      return updated;
    }));
  };

  const addFlight = () => {
    setFlights(prev => [...prev, { ...emptyFlight }]);
  };

  const removeFlight = (idx) => {
    setFlights(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveHotelDetails = async () => {
    try {
      await axios.put(`${API}/quotes/${id}/agent-update`, {
        hotels: hotelEntries,
        show_hotel_details: showHotelDetails
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Hotel details saved!");
      setIsEditingHotel(false);
      fetchQuoteDetail();
    } catch {
      toast.error("Failed to save hotel details");
    }
  };

  const updateHotelEntry = (idx, field, val) => {
    setHotelEntries(prev => prev.map((h, i) => {
      if (i !== idx) return h;
      const updated = { ...h, [field]: val };
      // Check-out auto-calculates from nights, but stays directly editable
      if (field === 'check_in') {
        if (updated.nights) updated.check_out = addNightsToDate(val, updated.nights);
      } else if (field === 'nights') {
        updated.check_out = addNightsToDate(updated.check_in, val);
      } else if (field === 'check_out') {
        updated.nights = calcHotelNights(updated.check_in, val);
      }
      return updated;
    }));
  };

  const addHotelEntry = () => {
    setHotelEntries(prev => {
      const last = prev[prev.length - 1];
      // First hotel's check-in auto-fills from the travel date; later hotels chain off the previous check-out
      const checkIn = prev.length === 0 ? toIsoDate(quote?.travel_date) : (last?.check_out || "");
      return [...prev, { ...emptyHotelEntry, check_in: checkIn }];
    });
  };

  const removeHotelEntry = (idx) => {
    setHotelEntries(prev => prev.filter((_, i) => i !== idx));
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: "bg-amber-100 text-amber-700", icon: Clock },
      quoted: { bg: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      approved: { bg: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      rejected: { bg: "bg-red-100 text-red-700", icon: XCircle },
      booked: { bg: "bg-blue-100 text-blue-700", icon: CheckCircle },
      revision_requested: { bg: "bg-orange-100 text-orange-700", icon: RotateCcw },
      confirmed: { bg: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      lost: { bg: "bg-red-100 text-red-700", icon: XCircle },
    };
    const config = styles[status] || { bg: "bg-gray-100 text-gray-700", icon: AlertCircle };
    const Icon = config.icon;
    return (
      <Badge className={`${config.bg} flex items-center gap-1 px-3 py-1`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="capitalize">{status}</span>
      </Badge>
    );
  };

  const getHotelStarLabel = (category) => {
    if (!category) return '-';
    const lower = category.toLowerCase();
    if (lower.includes('5')) return '5 Star';
    if (lower.includes('4')) return '4 Star';
    if (lower.includes('3')) return '3 Star';
    if (lower.includes('budget')) return 'Budget';
    return category;
  };

  // Fire one-time booking celebration confetti
  const triggerCelebration = () => {
    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ["#059669", "#10B981", "#34D399", "#6EE7B7", "#FFD700", "#FFF"];
    const frame = () => {
      confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
    setShowThankYou(true);
  };

  // Confirm booking with selected option (for multi-option quotes)
  const confirmBookingWithOption = async () => {
    if (confirmSelectedOption === null || confirmSelectedOption === undefined) {
      toast.error("Please select an option to confirm");
      return;
    }
    // Validate guest info
    const name = quote.client_name || guestForm.client_name;
    const phone = quote.client_phone || guestForm.client_phone;
    if (!name || !name.trim()) {
      toast.error("Guest Name is mandatory before confirmation. Please fill Guest Information.");
      setShowConfirmOptionModal(false);
      setIsEditingGuest(true);
      return;
    }
    if (!phone || !phone.trim()) {
      toast.error("Guest Mobile is mandatory before confirmation. Please fill Guest Information.");
      setShowConfirmOptionModal(false);
      setIsEditingGuest(true);
      return;
    }
    // GST toggle ON → GSTIN is mandatory
    if (gstRequired && !gstinValue.trim()) {
      toast.error("GSTIN is mandatory when GST is enabled. Please enter your GSTIN number.");
      setShowConfirmOptionModal(false);
      return;
    }
    try {
      setConfirmingBooking(true);
      await axios.put(`${API}/quotes/${id}/agent-update`, {
        status: 'booked',
        selected_option: confirmSelectedOption
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Option ${confirmSelectedOption + 1} confirmed! Admin has been notified.`);
      setShowConfirmOptionModal(false);
      triggerCelebration();
      fetchQuoteDetail();
    } catch (err) {
      toast.error(`Failed to confirm booking`);
    } finally {
      setConfirmingBooking(false);
    }
  };

  // Update quote status (Confirm/Lost)
  const updateQuoteStatus = async (newStatus) => {
    // Validate guest info before confirmation
    if (newStatus === 'booked') {
      const name = quote.client_name || guestForm.client_name;
      const phone = quote.client_phone || guestForm.client_phone;
      if (!name || !name.trim()) {
        toast.error("Guest Name is mandatory before confirmation. Please fill Guest Information.");
        setIsEditingGuest(true);
        return;
      }
      if (!phone || !phone.trim()) {
        toast.error("Guest Mobile is mandatory before confirmation. Please fill Guest Information.");
        setIsEditingGuest(true);
        return;
      }
      // GST toggle ON → GSTIN is mandatory
      if (gstRequired && !gstinValue.trim()) {
        toast.error("GSTIN is mandatory when GST is enabled. Please enter your GSTIN number.");
        return;
      }
    }
    try {
      await axios.put(`${API}/quotes/${id}/agent-update`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (newStatus === 'booked') {
        triggerCelebration();
      } else {
        toast.success(`Quote marked as ${newStatus}!`);
      }
      fetchQuoteDetail();
    } catch (err) {
      toast.error(`Failed to update status`);
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionNotes.trim()) {
      toast.error("Please provide revision notes");
      return;
    }
    try {
      setRequestingRevision(true);
      await axios.post(`${API}/quotes/${id}/request-revision`, {
        notes: revisionNotes.trim(),
        categories: revisionCategories
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Revision request sent to admin!");
      setShowRevisionModal(false);
      setRevisionNotes("");
      setRevisionCategories([]);
      fetchQuoteDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send revision request");
    } finally { setRequestingRevision(false); }
  };

  // ===== PDF & Word Generation Helpers =====
  const getAgentProfile = () => ({
    company_name: user?.company_name || quote?.agent_company || 'Travel Agent',
    name: user?.name || quote?.agent_name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    address: user?.address || '',
    city: user?.city || '',
    state: user?.state || '',
    website: user?.website || '',
    logo_url: user?.logo_url || '',
    profile_picture_url: user?.profile_picture_url || (typeof window !== 'undefined' ? localStorage.getItem('agentProfilePic') : '') || '',
    designation: user?.designation || ''
  });

  const getSellingPrice = (field) => {
    const basePrice = hasMultipleOptions ? (getActiveOption()[field] || 0) : (quote[field] || 0);
    if (markupMode === 'percentage') return basePrice + basePrice * (markupPercentage / 100);
    const markupMap = { quote_per_adult: agentMarkup.adult, quote_per_cwb: agentMarkup.cwb, quote_per_cnb: agentMarkup.cnb };
    return basePrice + (markupMap[field] || 0);
  };

  const getSellingTotal = () => {
    const adultRate = getSellingPrice('quote_per_adult');
    const cwbRate = getSellingPrice('quote_per_cwb');
    const cnbRate = getSellingPrice('quote_per_cnb');
    return (adultRate * (quote.pax_adults || 0)) + (cwbRate * (quote.cwb || 0)) + (cnbRate * (quote.cnb || 0));
  };

  // Generate PDF Document (returns jsPDF doc object)
  const generatePDFDoc = () => generateQuotePdfDoc(quote, getAgentProfile(), { agentMarkup, markupMode, markupPercentage, withPrice: true, agentView: true });

  // Generate PDF Download
  const downloadPDF = () => {
    const doc = generatePDFDoc();
    if (!doc) return;
    doc.save(`Quotation_${quote.quote_number || quote.id.slice(0, 8)}.pdf`);
    toast.success("PDF downloaded!");
  };

  // Share PDF via WhatsApp
  const shareViaWhatsApp = async () => {
    try {
      setSharingWhatsApp(true);
      toast.info("Generating PDF for sharing...");
      
      const doc = generatePDFDoc();
      if (!doc) {
        toast.error("Failed to generate PDF");
        return;
      }
      
      // Convert to blob
      const pdfBlob = doc.output('blob');
      const formData = new FormData();
      formData.append('file', pdfBlob, `Quotation_${quote.quote_number || quote.id.slice(0, 8)}.pdf`);
      
      // Upload to backend
      const uploadRes = await axios.post(`${API}/quotes/${id}/upload-pdf`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}${uploadRes.data.url}`;
      const phone = quote.client_phone ? quote.client_phone.replace(/\D/g, '') : '';
      const guestName = quote.client_name || 'Guest';
      const msg = `Hi ${guestName}! Here is your travel quotation for *${quote.destination || 'your trip'}* (${nights} Nights).\n\nDownload PDF: ${pdfUrl}\n\nRegards,\n${user?.company_name || user?.name || 'Travel Agent'}`;
      
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
      toast.success("WhatsApp opened with PDF link!");
    } catch (err) {
      console.error("WhatsApp share error:", err);
      toast.error("Failed to share via WhatsApp");
    } finally {
      setSharingWhatsApp(false);
    }
  };

  // Generate Word Download
  const downloadWord = () => {
    if (!quote) return;
    const agent = getAgentProfile();
    const sellingAdult = getSellingPrice('quote_per_adult');
    const sellingCwb = getSellingPrice('quote_per_cwb');
    const sellingCnb = getSellingPrice('quote_per_cnb');
    const sellingTotal = getSellingTotal();
    const activeOpt = getActiveOption();
    const allHotels = activeOpt.hotels || hotels || [];

    let html = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
      <head><meta charset="utf-8"><title>Quotation</title>
      <style>
        @page { margin: 20mm; }
        body { font-family: 'Segoe UI', Arial, sans-serif; color: #212121; line-height: 1.5; }
        .header { display: flex; margin-bottom: 20px; border-bottom: 2px solid #10B981; padding-bottom: 15px; }
        .header-logo { width: 100px; height: 60px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; border-radius: 6px; margin-right: 20px; color: #999; font-size: 11px; }
        .header-info { font-size: 12px; color: #787878; }
        .header-info .company-name { font-size: 18px; font-weight: bold; color: #212121; margin-bottom: 4px; }
        h2 { font-size: 22px; color: #212121; margin: 25px 0 5px 0; border-bottom: 2px solid #10B981; padding-bottom: 5px; }
        .card { background: #fafafa; border-radius: 8px; padding: 15px; margin: 10px 0; border: 1px solid #eee; }
        .card-title { font-size: 15px; font-weight: bold; color: #212121; margin-bottom: 10px; }
        .grid-3 { width: 100%; border-collapse: collapse; }
        .grid-3 td { width: 33%; padding: 5px 8px; vertical-align: top; }
        .label { font-size: 10px; color: #787878; margin: 0; }
        .value { font-size: 12px; font-weight: bold; color: #212121; margin: 2px 0 0 0; }
        .value.teal { color: #10B981; }
        .value.muted { color: #bbb; font-style: italic; font-weight: normal; }
        .icon-dot { display: inline-block; width: 8px; height: 8px; background: #10B981; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
        .day-header { background: #10B981; color: white; padding: 6px 14px; border-radius: 20px; font-weight: bold; font-size: 13px; display: inline-block; margin: 12px 0 6px 0; }
        .day-content { margin-left: 15px; padding: 8px 0; border-left: 2px solid #e0e0e0; padding-left: 15px; }
        .sightseeing { font-weight: bold; font-size: 12px; color: #212121; }
        .description { font-size: 11px; color: #787878; margin: 4px 0; }
        .overnight { font-size: 11px; color: #787878; margin-top: 6px; padding-top: 6px; border-top: 1px solid #eee; }
        .overnight strong { color: #212121; }
        .inc-exc-table { width: 100%; border-collapse: separate; border-spacing: 10px 0; }
        .inc-exc-table td { width: 50%; vertical-align: top; padding: 12px; border-radius: 8px; }
        .inc-card { border: 2px solid #10B981; }
        .inc-card .card-title { color: #10B981; }
        .exc-card { border: 2px solid #DC3C3C; }
        .exc-card .card-title { color: #DC3C3C; }
        .cost-row { padding: 6px 0; font-size: 13px; }
        .cost-label { font-weight: bold; }
        .cost-note { font-size: 10px; color: #999; font-style: italic; }
        .total-bar { background: #10B981; color: white; padding: 10px 15px; border-radius: 6px; font-size: 16px; font-weight: bold; margin-top: 10px; }
        .footer { text-align: center; color: #bbb; font-size: 10px; margin-top: 30px; border-top: 1px solid #eee; padding-top: 10px; }
      </style></head>
      <body>
        <!-- Header -->
        <table style="width:100%;margin-bottom:15px;border-bottom:2px solid #10B981;padding-bottom:12px;"><tr>
          <td style="width:100px;vertical-align:top;">
            <div style="width:80px;height:50px;background:#f0f0f0;border-radius:6px;text-align:center;line-height:50px;color:#999;font-size:10px;">LOGO</div>
          </td>
          <td style="vertical-align:top;">
            <div style="font-size:18px;font-weight:bold;color:#212121;">${agent.company_name}</div>
            ${agent.address ? `<div style="font-size:11px;color:#787878;">${agent.address}${agent.city ? ', ' + agent.city : ''}${agent.state ? ', ' + agent.state : ''}</div>` : ''}
            ${agent.phone ? `<div style="font-size:11px;color:#787878;">${agent.phone}</div>` : ''}
            ${agent.email ? `<div style="font-size:11px;color:#787878;">${agent.email}</div>` : ''}
            ${agent.website ? `<div style="font-size:11px;color:#787878;">${agent.website}</div>` : ''}
          </td>
        </tr></table>

        <h2>Quotation Details</h2>

        <!-- Trip Overview -->
        <div class="card">
          <div class="card-title">Trip Overview</div>
          <table class="grid-3"><tr>
            <td><p class="label"><span class="icon-dot"></span>Quotation No</p><p class="value teal">#${quote.quote_number || quote.id.slice(0, 8)}</p></td>
            <td><p class="label"><span class="icon-dot"></span>Destination</p><p class="value">${quote.destination || '-'}</p></td>
            <td><p class="label"><span class="icon-dot"></span>Travelers</p><p class="value">${quote.pax_adults || 0} Adults${(quote.cwb || 0) > 0 ? `, ${quote.cwb} CWB` : ''}${(quote.cnb || 0) > 0 ? `, ${quote.cnb} CNB` : ''}${(quote.pax_infants || 0) > 0 ? `, ${quote.pax_infants} Infants` : ''}</p>${(quote.cwb_ages && String(quote.cwb_ages).length > 0) || (quote.cnb_ages && String(quote.cnb_ages).length > 0) ? `<p style="font-size:9px;color:#999;">${quote.cwb_ages ? 'CWB Ages: ' + quote.cwb_ages + ' yrs' : ''}${quote.cwb_ages && quote.cnb_ages ? ' | ' : ''}${quote.cnb_ages ? 'CNB Ages: ' + quote.cnb_ages + ' yrs' : ''}</p>` : ''}</td>
          </tr><tr>
            <td><p class="label"><span class="icon-dot"></span>No of Nights</p><p class="value">${nights} Nights</p></td>
            <td><p class="label"><span class="icon-dot"></span>Travel Date</p><p class="value">${formatDate(quote.travel_date)}</p></td>
            <td><p class="label"><span class="icon-dot"></span>Return Date</p><p class="value">${formatDate(quote.return_date)}</p></td>
          </tr></table>
        </div>

        <!-- Guest Information -->
        <div class="card">
          <div class="card-title">Guest Information</div>
          <table class="grid-3"><tr>
            <td><p class="label">Guest Name</p><p class="value ${quote.client_name ? '' : 'muted'}">${quote.client_name || 'Not filled'}</p></td>
            <td><p class="label">Guest Mobile</p><p class="value ${quote.client_phone ? '' : 'muted'}">${quote.client_phone || 'Not filled'}</p></td>
          </tr></table>
        </div>

        ${(() => {
          const fList = Array.isArray(quote.flight_details) ? quote.flight_details : (quote.flight_details ? [quote.flight_details] : []);
          const vFlights = fList.filter(f => f.airline || f.flight_number);
          if (!quote.show_flight_details || vFlights.length === 0) return '';
          return `
        <!-- Flight Details -->
        <div class="card">
          <div class="card-title">Flight Details</div>
          ${vFlights.map((fd, fi) => {
            const isNonStop = (fd.connection_type || 'non_stop') === 'non_stop';
            return `
            ${fi > 0 ? '<hr style="border:none;border-top:1px solid #eee;margin:8px 0;">' : ''}
            <table style="width:100%;border-collapse:collapse;"><tr>
              <td style="width:25%;vertical-align:top;padding:8px;text-align:center;border-right:1px solid #eee;">
                <div style="font-weight:bold;font-size:13px;">${fd.airline || ''}</div>
                <div style="font-size:11px;color:#787878;">${fd.flight_number || ''}</div>
                ${fd.pnr ? `<div style="margin-top:6px;"><span style="background:#10B981;color:white;padding:2px 6px;border-radius:3px;font-size:9px;font-weight:bold;">PNR</span> <span style="color:#10B981;font-weight:bold;font-size:11px;border:1px solid #10B981;padding:2px 6px;border-radius:3px;">${fd.pnr}</span></div>` : ''}
              </td>
              <td style="width:30%;vertical-align:top;padding:8px;text-align:center;">
                <div style="font-size:16px;font-weight:bold;">${fd.departure_city || ''}</div>
                <div style="font-size:12px;"><strong>${fd.departure_code || ''}</strong> <strong style="font-size:14px;">${fd.departure_time || ''}</strong></div>
                <div style="font-size:10px;color:#999;">${fd.departure_date || ''}</div>
              </td>
              <td style="width:15%;vertical-align:middle;text-align:center;padding:8px;">
                <div style="font-size:10px;color:#555;">${fd.duration || ''}${isNonStop ? ' : Non Stop' : (fd.stops ? ' : ' + fd.stops + ' stop' : '')}</div>
                ${!isNonStop && fd.stop_type ? `<div style="font-size:9px;color:#999;">${fd.stop_type}</div>` : ''}
                ${!isNonStop && fd.stop_location ? `<div style="font-size:9px;color:#999;">${fd.stop_location}</div>` : ''}
              </td>
              <td style="width:30%;vertical-align:top;padding:8px;text-align:center;">
                <div style="font-size:16px;font-weight:bold;">${fd.arrival_city || ''}</div>
                <div style="font-size:12px;"><strong style="font-size:14px;">${fd.arrival_time || ''}</strong> <strong>${fd.arrival_code || ''}</strong></div>
                <div style="font-size:10px;color:#999;">${fd.arrival_date || ''}</div>
              </td>
            </tr></table>`;
          }).join('')}
        </div>`;
        })()}

        <!-- Hotel Details -->
        <div class="card">
          <div class="card-title">Hotel Details</div>
          ${allHotels.length > 0 ? allHotels.map(h => {
            const extras = [];
            if (h.extra_bed_toggle && h.extra_bed_count > 0) extras.push(`EB/EM: ${h.extra_bed_count}`);
            if (h.cwb_toggle && h.cwb_count > 0) extras.push(`CWB: ${h.cwb_count}`);
            if (h.cnb_toggle && h.cnb_count > 0) extras.push(`CNB: ${h.cnb_count}`);
            return `
            <div style="margin-bottom:8px;">
              <div style="font-weight:bold;font-size:13px;">${h.hotel_name || h.name || '-'}</div>
              <div style="font-size:11px;color:#787878;">${h.hotel_category || quote.hotel_category || ''} | ${h.city || quote.destination || ''}</div>
              <div style="font-size:10px;color:#999;">Rooms: ${h.rooms_count || h.rooms || quote.no_of_rooms || 1} | Check-in: ${formatDate(h.check_in)} | Check-out: ${formatDate(h.check_out)} | Meal: ${(h.meal_plan || quote.meal_plan || '-').toUpperCase()}</div>
              ${extras.length > 0 ? `<div style="font-size:10px;color:#999;">${extras.join(' | ')}</div>` : ''}
            </div>`;
          }).join('') : quote.hotel_name ? `
            <div><div style="font-weight:bold;">${quote.hotel_name}</div><div style="font-size:11px;color:#787878;">${getHotelStarLabel(quote.hotel_category)} | ${quote.destination || ''}</div></div>
          ` : '<p style="color:#bbb;text-align:center;font-style:italic;">No hotel details available</p>'}
        </div>`;

    // Day-wise Itinerary
    if (itinerary && itinerary.length > 0) {
      html += `<h2>Day-wise Itinerary</h2>`;
      itinerary.forEach((day, idx) => {
        const dd = parseDayDate(day.date, quote.travel_date, idx);
        const dateLabel = formatDayDateLabel(dd);
        const cityName = day.city || day.title || '';
        const isLastDay = idx === itinerary.length - 1;
        html += `<div class="day-header">DAY ${day.day || idx + 1}: ${dateLabel}${cityName && !isLastDay ? ' - ' + cityName.toUpperCase() : ''}</div>`;
        html += `<div class="day-content">`;
        // Activities (direct headline + description, no bullets)
        if (day.activities && Array.isArray(day.activities)) {
          day.activities.forEach(act => {
            const actName = typeof act === 'string' ? act : (act.name || act.title || '');
            const actDesc = typeof act === 'object' ? act.description : '';
            if (actName) html += `<div style="margin:4px 0;"><strong style="font-size:12px;color:#212121;">${actName}</strong>${actDesc ? `<div style="font-size:11px;color:#787878;margin-top:2px;">${actDesc}</div>` : ''}</div>`;
          });
        }
        // Description (if no activities)
        if (day.description && (!day.activities || day.activities.length === 0)) {
          html += `<div class="description">${day.description}</div>`;
        }
        // Transfer — shown unless the day is explicitly marked "no transfer"
        const vehicleName = day.vehicle || '';
        const rawXfer = (day.transfer_type || '').toLowerCase();
        const isNoTransfer = rawXfer === 'none' || rawXfer === 'no_transfer' || rawXfer === 'no-transfer';
        const transferLabel = rawXfer === 'sic' || rawXfer === 'seat_in_coach' || rawXfer === 'seat-in-coach'
          ? 'Seat in Coach (SIC)'
          : 'Private';
        if (!isNoTransfer) {
          html += `<div style="font-size:11px;color:#787878;margin-top:6px;">Transfer: <strong style="color:#212121;">${transferLabel}</strong></div>`;
        }
        if (vehicleName) {
          html += `<div style="font-size:11px;color:#787878;">Vehicle: <strong style="color:#212121;">${vehicleName}</strong></div>`;
        }
        if (day.hotel) html += `<div class="overnight">Accommodation: <strong>${day.hotel}</strong></div>`;
        html += `</div>`;
      });
    }

    // Inclusions & Exclusions
    html += `
      <table class="inc-exc-table"><tr>
        <td class="inc-card">
          <div class="card-title">&#10003; Inclusions</div>
          ${quote.inclusions ? `<pre style="white-space:pre-wrap;font-family:inherit;font-size:11px;color:#555;margin:0;">${quote.inclusions}</pre>` : '<p style="color:#bbb;font-style:italic;">No inclusions specified</p>'}
        </td>
        <td class="exc-card">
          <div class="card-title">&#10007; Exclusions</div>
          ${quote.exclusions ? `<pre style="white-space:pre-wrap;font-family:inherit;font-size:11px;color:#555;margin:0;">${quote.exclusions}</pre>` : '<p style="color:#bbb;font-style:italic;">No exclusions specified</p>'}
        </td>
      </tr></table>`;

    // Terms & Conditions
    if (quote.show_terms_conditions && quote.terms_conditions) {
      html += `
        <div class="card" style="border-left: 3px solid #4F46E5; margin-top: 15px;">
          <div class="card-title" style="color: #4F46E5;">&#167; Terms & Conditions</div>
          <pre style="white-space:pre-wrap;font-family:inherit;font-size:11px;color:#555;margin:0;">${quote.terms_conditions}</pre>
        </div>`;
    }

    // Cost Breakdown
    html += `<h2>Cost Breakdown</h2>`;
    if (sellingAdult > 0) {
      html += `<div class="cost-row"><span class="cost-label">PER ADULT COST:</span> INR ${formatCurrency(sellingAdult).replace('₹','')} x ${quote.pax_adults || 0} <span class="cost-note">(Selling price per adult)</span></div>`;
    }
    if (sellingCwb > 0 && (quote.cwb || 0) > 0) {
      html += `<div class="cost-row"><span class="cost-label">PER CWB COST:</span> INR ${formatCurrency(sellingCwb).replace('₹','')} x ${quote.cwb || 0} <span class="cost-note">(Child with bed)</span></div>`;
    }
    if (sellingCnb > 0 && (quote.cnb || 0) > 0) {
      html += `<div class="cost-row"><span class="cost-label">PER CNB COST:</span> INR ${formatCurrency(sellingCnb).replace('₹','')} x ${quote.cnb || 0} <span class="cost-note">(Child no bed)</span></div>`;
    }
    html += `<div class="total-bar">TOTAL COST: INR ${formatCurrency(sellingTotal || quote.final_total_amount || quote.quote_amount || 0).replace('₹','')}</div>`;
    html += `<div class="footer">Generated on ${(() => { const d = new Date(); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()} | ${agent.company_name}</div>`;
    html += `</body></html>`;

    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Quotation_${quote.quote_number || quote.id.slice(0, 8)}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Word document downloaded!");
  };

  if (loading) {
    return (
      <DashboardLayout type="agent">
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout type="agent">
        <div className="flex flex-col items-center justify-center py-20">
          <XCircle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Quote</h2>
          <p className="text-gray-500">{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => window.close()}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Close Tab
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  if (!quote) return null;

  const nights = quote.nights || calculateNights(quote.travel_date, quote.return_date);
  const activeOption = getActiveOption();
  const hotels = Array.isArray(activeOption.hotels) && activeOption.hotels.length > 0 
    ? activeOption.hotels 
    : (Array.isArray(quote.hotels) ? quote.hotels : []);
  const activities = Array.isArray(quote.activities) ? quote.activities : [];
  
  // Build itinerary: use saved itinerary if available, otherwise build from hotels & activities
  const buildItineraryFromData = () => {
    if (!quote.travel_date || !quote.return_date) return [];
    
    const start = new Date(quote.travel_date);
    const end = new Date(quote.return_date);
    const daysList = [];
    let dayNum = 1;
    const transfersDetails = quote.transfers_details || [];
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      
      // Find hotel for this day
      const hotelForDay = hotels.find(h => {
        if (!h.check_in || !h.check_out) return false;
        const checkIn = new Date(h.check_in);
        const checkOut = new Date(h.check_out);
        const current = new Date(dateStr);
        checkIn.setHours(0,0,0,0);
        checkOut.setHours(0,0,0,0);
        current.setHours(0,0,0,0);
        return current >= checkIn && current < checkOut;
      });
      
      // Find activities for this day
      const activitiesForDay = activities.filter(a => a.day === dayNum);
      
      // Find transfer for this day
      const transferForDay = transfersDetails.find(t => t.day === dayNum);
      
      daysList.push({
        day: dayNum,
        date: dateStr,
        title: hotelForDay?.city || quote.destination || `Day ${dayNum}`,
        city: hotelForDay?.city || '',
        hotel: hotelForDay ? `${hotelForDay.hotel_name} (${hotelForDay.hotel_category})` : null,
        activities: activitiesForDay.map(a => ({ name: a.tour_name || a.name || a.title, type: a.type })),
        sightseeing: activitiesForDay.map(a => a.tour_name || a.name || a.title).filter(Boolean).join(', '),
        transfer_type: transferForDay?.transfer_type || null,
        vehicle: transferForDay?.type || null,
        description: activitiesForDay.length > 0 
          ? `Sightseeing: ${activitiesForDay.map(a => a.tour_name || a.name || a.title).filter(Boolean).join(', ')}`
          : (hotelForDay ? `Stay at ${hotelForDay.hotel_name}` : '')
      });
      
      dayNum++;
    }
    
    return daysList;
  };
  
  // Use saved itinerary if it has data, otherwise build from hotels & activities
  const itinerary = (Array.isArray(quote.itinerary) && quote.itinerary.length > 0) 
    ? quote.itinerary 
    : buildItineraryFromData();

  return (
    <DashboardLayout type="agent">
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="quote-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-emerald-600 font-bold bg-emerald-50 px-3 py-1.5 rounded-lg text-lg">
                #{quote.quote_number || quote.id.slice(0, 8).toUpperCase()}
              </span>
              {getStatusBadge(quote.status)}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Quotation Details</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Confirm & Lost Buttons — visible whenever admin has filled in a quote_amount,
                regardless of whether they clicked the FINAL "Save Quote" (which sets status='quoted').
                Some admins save intermediate steps which leaves status='pending' even with prices filled. */}
            {(() => {
              const adminQuoted = Number(quote.quote_amount || quote.final_total_amount || 0) > 0;
              const finalised = quote.status === 'booked' || quote.status === 'confirmed' || quote.status === 'lost' || quote.status === 'rejected';
              if (!adminQuoted && !finalised) {
                return (
                  <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1.5" data-testid="awaiting-admin-quote-badge">
                    <Clock className="w-4 h-4 mr-1.5" /> Awaiting admin quotation
                  </Badge>
                );
              }
              return null;
            })()}
            {/* Confirm Booking — once admin has set a quote_amount */}
            {Number(quote.quote_amount || quote.final_total_amount || 0) > 0
              && quote.status !== 'booked' && quote.status !== 'confirmed'
              && quote.status !== 'lost' && quote.status !== 'rejected' && (() => {
              const hasGuest = !!((quote?.client_name || guestForm.client_name)?.trim() && (quote?.client_phone || guestForm.client_phone)?.trim());
              return (
                <Button
                  onClick={() => {
                    if (hasMultipleOptions) {
                      setConfirmSelectedOption(null);
                      setShowConfirmOptionModal(true);
                    } else {
                      updateQuoteStatus('booked');
                    }
                  }}
                  disabled={!hasGuest}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title={hasGuest ? "Confirm" : "Fill Guest Name and Mobile to enable confirmation"}
                  data-testid="confirm-quote-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> {hasGuest ? 'Confirm' : 'Add Guest Info First'}
                </Button>
              );
            })()}
            {(quote.status === 'approved' || quote.status === 'quoted') && (
              <Button
                onClick={() => setShowRevisionModal(true)}
                variant="outline"
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
                data-testid="request-revision-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Request Revision
              </Button>
            )}
            {quote.status === 'revision_requested' && (
              <Badge className="bg-orange-100 text-orange-700 text-sm px-3 py-1.5">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Revision Requested
              </Badge>
            )}
            {/* Lost Button — once admin has set a quote_amount */}
            {Number(quote.quote_amount || quote.final_total_amount || 0) > 0
              && quote.status !== 'lost' && quote.status !== 'rejected' && quote.status !== 'booked' && quote.status !== 'confirmed' && (
              <Button 
                onClick={() => updateQuoteStatus('lost')} 
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="lost-quote-btn"
              >
                <XCircle className="w-4 h-4 mr-2" /> Lost
              </Button>
            )}
            
            <Button variant="outline" onClick={() => window.close()}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Close
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Overview */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Plane className="w-5 h-5 text-emerald-600" />
                  Trip Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Quotation No</p>
                      <p className="font-semibold text-emerald-600">
                        #{quote.quote_number || quote.id.slice(0, 8).toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Destination</p>
                      <p className="font-semibold text-gray-800">{quote.destination || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Travelers</p>
                      <p className="font-semibold text-gray-800">
                        {quote.pax_adults || 0} Adults{(quote.cwb || 0) > 0 ? `, ${quote.cwb} CWB` : ''}{(quote.cnb || 0) > 0 ? `, ${quote.cnb} CNB` : ''}{(quote.pax_infants || 0) > 0 ? `, ${quote.pax_infants} Infants` : ''}
                      </p>
                      {quote.cwb_ages && String(quote.cwb_ages).length > 0 && (
                        <p className="text-xs text-gray-400 mt-0.5">CWB Ages: {quote.cwb_ages} yrs</p>
                      )}
                      {quote.cnb_ages && String(quote.cnb_ages).length > 0 && (
                        <p className="text-xs text-gray-400 mt-0.5">CNB Ages: {quote.cnb_ages} yrs</p>
                      )}
                      {quote.infant_ages && String(quote.infant_ages).length > 0 && (
                        <p className="text-xs text-gray-400 mt-0.5">Infant Ages: {quote.infant_ages} yrs</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Moon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">No of Nights</p>
                      <p className="font-semibold text-gray-800">{nights} Nights</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Travel Date</p>
                      <p className="font-semibold text-gray-800">{formatDate(quote.travel_date)}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Return Date</p>
                      <p className="font-semibold text-gray-800">{formatDate(quote.return_date)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guest Information */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="guest-info-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Guest Information
                    {quote.status !== 'booked' && quote.status !== 'confirmed' && (
                      <span className="text-xs text-red-500 font-normal ml-1">* Required for confirmation</span>
                    )}
                  </div>
                  {!isEditingGuest ? (
                    <Button variant="ghost" size="sm" onClick={() => setIsEditingGuest(true)} data-testid="edit-guest-info-btn">
                      <Edit3 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={handleSaveGuestDetails} className="text-emerald-600" data-testid="save-guest-info-btn">
                      <Save className="w-4 h-4 mr-1" /> Save
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditingGuest ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-gray-500">Guest Name <span className="text-red-500">*</span></Label>
                        <Input
                          value={guestForm.client_name}
                          onChange={(e) => setGuestForm({ ...guestForm, client_name: e.target.value })}
                          placeholder="Enter guest name"
                          className={`mt-1 ${!guestForm.client_name ? 'border-red-300' : ''}`}
                          data-testid="guest-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-500">Guest Mobile <span className="text-red-500">*</span></Label>
                        <Input
                          value={guestForm.client_phone}
                          onChange={(e) => setGuestForm({ ...guestForm, client_phone: e.target.value })}
                          placeholder="+91 9876543210"
                          className={`mt-1 ${!guestForm.client_phone ? 'border-red-300' : ''}`}
                          data-testid="guest-mobile-input"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">Guest Name</p>
                          <p className="font-semibold text-gray-800">
                            {quote.client_name || <span className="text-red-400 italic">Not filled *</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">Guest Mobile</p>
                          {quote.client_phone ? (
                            <a href={`tel:${quote.client_phone}`} className="font-semibold text-emerald-600 hover:underline">
                              {quote.client_phone}
                            </a>
                          ) : (
                            <span className="text-red-400 italic">Not filled *</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Flight Details */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="flight-details-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    <Plane className="w-5 h-5 text-sky-600" />
                    Flight Details
                  </div>
                  <div className="flex items-center gap-3">
                    {showFlightDetails && !isEditingFlight && (
                      <Button variant="ghost" size="sm" onClick={() => setIsEditingFlight(true)} data-testid="edit-flight-btn">
                        <Edit3 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                    )}
                    {showFlightDetails && isEditingFlight && (
                      <Button variant="ghost" size="sm" onClick={handleSaveFlightDetails} className="text-emerald-600" data-testid="save-flight-btn">
                        <Save className="w-4 h-4 mr-1" /> Save
                      </Button>
                    )}
                    <button
                      onClick={() => handleToggleFlightDetails(!showFlightDetails)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${showFlightDetails ? 'bg-sky-500' : 'bg-gray-300'}`}
                      data-testid="flight-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${showFlightDetails ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                </CardTitle>
              </CardHeader>
              {showFlightDetails && (
                <CardContent>
                  {isEditingFlight ? (
                    <div className="space-y-6" data-testid="flight-edit-form">
                      {flights.map((fl, idx) => (
                        <div key={idx} className={`space-y-4 ${idx > 0 ? 'pt-5 border-t border-gray-200' : ''}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold text-gray-700">Flight {idx + 1}</p>
                            {flights.length > 1 && (
                              <Button variant="ghost" size="sm" className="text-red-500 text-xs h-7" onClick={() => removeFlight(idx)} data-testid={`remove-flight-${idx}`}>Remove</Button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <Label className="text-xs text-gray-500">Airline</Label>
                              <Input value={fl.airline} onChange={e => updateFlight(idx, 'airline', e.target.value)} placeholder="e.g. IndiGo" className="mt-1 h-9 text-sm" />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Flight Number</Label>
                              <Input value={fl.flight_number} onChange={e => updateFlight(idx, 'flight_number', e.target.value)} placeholder="e.g. 6E-1607" className="mt-1 h-9 text-sm" />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">PNR</Label>
                              <Input value={fl.pnr} onChange={e => updateFlight(idx, 'pnr', e.target.value)} placeholder="e.g. EBSY3H" className="mt-1 h-9 text-sm" />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Connection</Label>
                              <select
                                value={fl.connection_type || 'non_stop'}
                                onChange={e => updateFlight(idx, 'connection_type', e.target.value)}
                                className="mt-1 h-9 text-sm w-full rounded-md border border-gray-200 px-3 bg-white"
                                data-testid={`connection-type-${idx}`}
                              >
                                <option value="non_stop">Non Stop</option>
                                <option value="with_stops">With Stops</option>
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3 p-3 bg-sky-50/50 rounded-lg border border-sky-100">
                              <p className="text-xs font-semibold text-sky-700 uppercase">Departure</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div><Label className="text-xs text-gray-500">City</Label><Input value={fl.departure_city} onChange={e => updateFlight(idx, 'departure_city', e.target.value)} placeholder="Mumbai" className="mt-1 h-8 text-sm" /></div>
                                <div><Label className="text-xs text-gray-500">Airport Code</Label><Input value={fl.departure_code} onChange={e => updateFlight(idx, 'departure_code', e.target.value)} placeholder="BOM" className="mt-1 h-8 text-sm" /></div>
                                <div><Label className="text-xs text-gray-500">Time</Label><Input value={fl.departure_time} onChange={e => updateFlight(idx, 'departure_time', e.target.value)} placeholder="01:40 hrs" className="mt-1 h-8 text-sm" /></div>
                                <div><Label className="text-xs text-gray-500">Date</Label><Input type="date" value={fl.departure_date_iso || ''} onChange={e => { updateFlight(idx, 'departure_date_iso', e.target.value); const d = new Date(e.target.value + 'T00:00:00'); if (!isNaN(d)) updateFlight(idx, 'departure_date', `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`); }} className="mt-1 h-8 text-sm" /></div>
                              </div>
                            </div>
                            <div className="space-y-3 p-3 bg-emerald-50/50 rounded-lg border border-emerald-100">
                              <p className="text-xs font-semibold text-emerald-700 uppercase">Arrival</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div><Label className="text-xs text-gray-500">City</Label><Input value={fl.arrival_city} onChange={e => updateFlight(idx, 'arrival_city', e.target.value)} placeholder="Denpasar Bali" className="mt-1 h-8 text-sm" /></div>
                                <div><Label className="text-xs text-gray-500">Airport Code</Label><Input value={fl.arrival_code} onChange={e => updateFlight(idx, 'arrival_code', e.target.value)} placeholder="DPS" className="mt-1 h-8 text-sm" /></div>
                                <div><Label className="text-xs text-gray-500">Time</Label><Input value={fl.arrival_time} onChange={e => updateFlight(idx, 'arrival_time', e.target.value)} placeholder="13:10 hrs" className="mt-1 h-8 text-sm" /></div>
                                <div><Label className="text-xs text-gray-500">Date</Label><Input type="date" value={fl.arrival_date_iso || ''} onChange={e => { updateFlight(idx, 'arrival_date_iso', e.target.value); const d = new Date(e.target.value + 'T00:00:00'); if (!isNaN(d)) updateFlight(idx, 'arrival_date', `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`); }} className="mt-1 h-8 text-sm" /></div>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div><Label className="text-xs text-gray-500">Duration</Label><Input value={fl.duration} onChange={e => updateFlight(idx, 'duration', e.target.value)} placeholder="9h 0m" className="mt-1 h-9 text-sm" /></div>
                            {(fl.connection_type || 'non_stop') === 'with_stops' && (
                              <>
                                <div><Label className="text-xs text-gray-500">Stops</Label><Input value={fl.stops} onChange={e => updateFlight(idx, 'stops', e.target.value)} placeholder="1" className="mt-1 h-9 text-sm" /></div>
                                <div><Label className="text-xs text-gray-500">Stop Type</Label><Input value={fl.stop_type} onChange={e => updateFlight(idx, 'stop_type', e.target.value)} placeholder="Technical stop" className="mt-1 h-9 text-sm" /></div>
                                <div><Label className="text-xs text-gray-500">Stop Location</Label><Input value={fl.stop_location} onChange={e => updateFlight(idx, 'stop_location', e.target.value)} placeholder="Chennai" className="mt-1 h-9 text-sm" /></div>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={addFlight} className="w-full border-dashed border-sky-300 text-sky-600 hover:bg-sky-50" data-testid="add-flight-btn">
                        <Plane className="w-4 h-4 mr-2" /> Add Flight
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="flight-display">
                      {flights.filter(f => f.airline || f.flight_number).length > 0 ? (
                        flights.filter(f => f.airline || f.flight_number).map((fl, idx) => (
                          <div key={idx} className={`p-4 bg-gray-50 rounded-xl border border-gray-100 ${idx > 0 ? '' : ''}`}>
                            {flights.filter(f => f.airline || f.flight_number).length > 1 && (
                              <p className="text-[10px] font-bold text-sky-600 uppercase tracking-wider mb-2">Flight {idx + 1}</p>
                            )}
                            <div className="flex flex-col md:flex-row items-stretch gap-4">
                              {/* Airline & PNR */}
                              <div className="flex flex-col items-center justify-center gap-2 min-w-[140px] pr-4 md:border-r border-gray-200">
                                <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                                  <Plane className="w-5 h-5 text-sky-600" />
                                </div>
                                <p className="font-semibold text-gray-900 text-sm">{fl.airline || '-'}</p>
                                <p className="text-xs text-gray-500">{fl.flight_number || '-'}</p>
                                {fl.pnr && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <span className="text-[10px] font-bold text-white bg-emerald-500 px-2 py-0.5 rounded">PNR</span>
                                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">{fl.pnr}</span>
                                  </div>
                                )}
                              </div>

                              {/* Departure */}
                              <div className="flex-1 text-center">
                                <p className="text-xl font-bold text-gray-900">{fl.departure_city || '-'}</p>
                                <p className="text-sm text-gray-800 mt-1">
                                  <span className="font-semibold">{fl.departure_code}</span>
                                  {fl.departure_time && <span className="ml-2 text-lg font-bold">{fl.departure_time}</span>}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">{fl.departure_date}</p>
                              </div>

                              {/* Connection line */}
                              <div className="flex flex-col items-center justify-center gap-1 px-3">
                                <div className="flex items-center gap-2 w-full">
                                  <div className="w-2 h-2 rounded-full border-2 border-sky-500" />
                                  <div className="flex-1 border-t-2 border-dashed border-gray-300 relative">
                                    <Plane className="w-4 h-4 text-sky-500 absolute -top-2 left-1/2 -translate-x-1/2" />
                                  </div>
                                  <div className="w-2 h-2 rounded-full bg-sky-500" />
                                </div>
                                {fl.duration && <p className="text-xs text-gray-500 font-medium">{fl.duration}{(fl.connection_type || 'non_stop') === 'non_stop' ? ' : Non Stop' : (fl.stops ? ` : ${fl.stops} stop` : '')}</p>}
                                {(fl.connection_type || 'non_stop') === 'with_stops' && fl.stop_type && <p className="text-[10px] text-gray-400">{fl.stop_type}</p>}
                                {(fl.connection_type || 'non_stop') === 'with_stops' && fl.stop_location && <p className="text-[10px] text-gray-400">{fl.stop_location}</p>}
                              </div>

                              {/* Arrival */}
                              <div className="flex-1 text-center">
                                <p className="text-xl font-bold text-gray-900">{fl.arrival_city || '-'}</p>
                                <p className="text-sm text-gray-800 mt-1">
                                  {fl.arrival_time && <span className="text-lg font-bold mr-2">{fl.arrival_time}</span>}
                                  <span className="font-semibold">{fl.arrival_code}</span>
                                </p>
                                <p className="text-xs text-gray-400 mt-1">{fl.arrival_date}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-4">No flight details added. Click Edit to add flight information.</p>
                      )}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Hotel Details */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="hotel-details-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    <Hotel className="w-5 h-5 text-purple-600" />
                    Hotel Details
                  </div>
                  <div className="flex items-center gap-3">
                    {showHotelDetails && !isEditingHotel && (
                      <Button variant="ghost" size="sm" onClick={() => setIsEditingHotel(true)} data-testid="edit-hotel-btn">
                        <Edit3 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                    )}
                    {showHotelDetails && isEditingHotel && (
                      <Button variant="ghost" size="sm" onClick={handleSaveHotelDetails} className="text-emerald-600" data-testid="save-hotel-btn">
                        <Save className="w-4 h-4 mr-1" /> Save
                      </Button>
                    )}
                    <button
                      onClick={() => handleToggleHotelDetails(!showHotelDetails)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${showHotelDetails ? 'bg-purple-500' : 'bg-gray-300'}`}
                      data-testid="hotel-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${showHotelDetails ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                </CardTitle>
              </CardHeader>
              {showHotelDetails && (
              <CardContent>
                {isEditingHotel ? (
                  <div className="space-y-6" data-testid="hotel-edit-form">
                    {hotelEntries.map((h, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-700">Hotel {idx + 1}</p>
                          {hotelEntries.length > 1 && (
                            <Button variant="ghost" size="sm" className="text-red-500 text-xs h-7" onClick={() => removeHotelEntry(idx)} data-testid={`remove-hotel-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">City</Label>
                            <Input value={h.city} onChange={e => updateHotelEntry(idx, 'city', e.target.value)} placeholder="e.g. Bali" className="mt-1 h-9 text-sm" data-testid={`hotel-city-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Hotel Name</Label>
                            <Input value={h.hotel_name} onChange={e => updateHotelEntry(idx, 'hotel_name', e.target.value)} placeholder="e.g. Ubud Resort & Spa" className="mt-1 h-9 text-sm" data-testid={`hotel-name-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Star Category</Label>
                            <select
                              value={h.hotel_category}
                              onChange={e => updateHotelEntry(idx, 'hotel_category', e.target.value)}
                              className="mt-1 h-9 text-sm w-full rounded-md border border-gray-200 px-3 bg-white"
                              data-testid={`hotel-category-${idx}`}
                            >
                              <option value="2 Star">2 Star</option>
                              <option value="3 Star">3 Star</option>
                              <option value="3 Star Deluxe">3 Star Deluxe</option>
                              <option value="4 Star">4 Star</option>
                              <option value="4 Star Deluxe">4 Star Deluxe</option>
                              <option value="5 Star">5 Star</option>
                              <option value="5 Star Luxury">5 Star Luxury</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Room Category</Label>
                            <Input value={h.room_type} onChange={e => updateHotelEntry(idx, 'room_type', e.target.value)} placeholder="e.g. Deluxe Room" className="mt-1 h-9 text-sm" data-testid={`hotel-room-type-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Meal Plan</Label>
                            <select
                              value={h.meal_plan}
                              onChange={e => updateHotelEntry(idx, 'meal_plan', e.target.value)}
                              className="mt-1 h-9 text-sm w-full rounded-md border border-gray-200 px-3 bg-white"
                              data-testid={`hotel-meal-plan-${idx}`}
                            >
                              <option value="EP">EP (Room Only)</option>
                              <option value="CP">CP (Breakfast)</option>
                              <option value="MAP">MAP (B+L/D)</option>
                              <option value="AP">AP (All Meals)</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">No. of Rooms</Label>
                            <Input type="number" min="1" value={h.rooms_count} onChange={e => updateHotelEntry(idx, 'rooms_count', parseInt(e.target.value) || 1)} className="mt-1 h-9 text-sm" data-testid={`hotel-rooms-count-${idx}`} />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Check In</Label>
                            <Input type="date" value={h.check_in} onChange={e => updateHotelEntry(idx, 'check_in', e.target.value)} className="mt-1 h-9 text-sm" data-testid={`hotel-check-in-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">No. of Nights</Label>
                            <Input type="number" min="0" value={h.nights} onChange={e => updateHotelEntry(idx, 'nights', parseInt(e.target.value) || 0)} className="mt-1 h-9 text-sm" data-testid={`hotel-nights-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Check Out</Label>
                            <Input type="date" value={h.check_out} onChange={e => updateHotelEntry(idx, 'check_out', e.target.value)} className="mt-1 h-9 text-sm" data-testid={`hotel-check-out-${idx}`} />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-gray-200">
                          <div className="flex items-center gap-2">
                            <input type="checkbox" checked={h.extra_bed_toggle} onChange={e => updateHotelEntry(idx, 'extra_bed_toggle', e.target.checked)} className="h-4 w-4" data-testid={`hotel-extra-bed-toggle-${idx}`} />
                            <Label className="text-xs text-gray-500">Extra Bed</Label>
                            {h.extra_bed_toggle && (
                              <Input type="number" min="0" value={h.extra_bed_count} onChange={e => updateHotelEntry(idx, 'extra_bed_count', parseInt(e.target.value) || 0)} className="h-8 text-sm w-20" data-testid={`hotel-extra-bed-count-${idx}`} />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <input type="checkbox" checked={h.cwb_toggle} onChange={e => updateHotelEntry(idx, 'cwb_toggle', e.target.checked)} className="h-4 w-4" data-testid={`hotel-cwb-toggle-${idx}`} />
                            <Label className="text-xs text-gray-500">CWB</Label>
                            {h.cwb_toggle && (
                              <Input type="number" min="0" value={h.cwb_count} onChange={e => updateHotelEntry(idx, 'cwb_count', parseInt(e.target.value) || 0)} className="h-8 text-sm w-20" data-testid={`hotel-cwb-count-${idx}`} />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <input type="checkbox" checked={h.cnb_toggle} onChange={e => updateHotelEntry(idx, 'cnb_toggle', e.target.checked)} className="h-4 w-4" data-testid={`hotel-cnb-toggle-${idx}`} />
                            <Label className="text-xs text-gray-500">CNB</Label>
                            {h.cnb_toggle && (
                              <Input type="number" min="0" value={h.cnb_count} onChange={e => updateHotelEntry(idx, 'cnb_count', parseInt(e.target.value) || 0)} className="h-8 text-sm w-20" data-testid={`hotel-cnb-count-${idx}`} />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addHotelEntry} className="w-full border-dashed border-purple-300 text-purple-600 hover:bg-purple-50" data-testid="add-hotel-btn">
                      <Plus className="w-4 h-4 mr-2" /> Add Hotel
                    </Button>
                  </div>
                ) : hasMultipleOptions ? (
                  <div className="space-y-10">
                    {accommodationOptions.map((opt, optIdx) => {
                      const optHotels = opt.hotels || [];
                      const optBadgeColor = optIdx === 0 ? 'bg-blue-100 text-blue-700' : optIdx === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
                      return (
                        <div key={`opt-group-${optIdx}`} data-testid={`hotel-option-group-${optIdx}`} className={optIdx > 0 ? "pt-6 border-t border-gray-200" : ""}>
                          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                            <Badge className={`${optBadgeColor} text-xs font-bold tracking-wider`}>OPTION {optIdx + 1}</Badge>
                          </div>
                          {optHotels.length > 0 ? (
                            <div className="space-y-4">
                              {optHotels.map((hotel, idx) => (
                                <div key={`${optIdx}-${idx}`} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                                  <div className="flex justify-between items-start mb-3">
                                    <div>
                                      <p className="font-semibold text-gray-800 text-lg">{hotel.hotel_name || hotel.name}</p>
                                      <p className="text-sm text-gray-500">{hotel.city || hotel.destination}</p>
                                    </div>
                                    <Badge className="bg-purple-100 text-purple-700">
                                      {getHotelStarLabel(hotel.category || hotel.hotel_category || quote.hotel_category)}
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                                    <div>
                                      <p className="text-gray-500">No of Rooms</p>
                                      <p className="font-medium text-gray-800">{hotel.rooms_count || hotel.rooms || quote.no_of_rooms || 1}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">Room Category</p>
                                      <p className="font-medium text-gray-800">{hotel.room_type || quote.room_type || '-'}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">Meal Plan</p>
                                      <p className="font-medium text-gray-800">{(hotel.meal_plan || quote.meal_plan || '-').toUpperCase()}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">Nights</p>
                                      <p className="font-medium text-gray-800">{hotel.nights || '-'}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">Check In</p>
                                      <p className="font-medium text-gray-800">{formatDate(hotel.check_in)}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">Check Out</p>
                                      <p className="font-medium text-gray-800">{formatDate(hotel.check_out)}</p>
                                    </div>
                                    {hotel.extra_bed_toggle && hotel.extra_bed_count > 0 && (
                                      <div>
                                        <p className="text-gray-500">EB / EM</p>
                                        <p className="font-medium text-gray-800">{hotel.extra_bed_count}</p>
                                      </div>
                                    )}
                                    {hotel.cwb_toggle && hotel.cwb_count > 0 && (
                                      <div>
                                        <p className="text-gray-500">CWB</p>
                                        <p className="font-medium text-gray-800">{hotel.cwb_count}</p>
                                      </div>
                                    )}
                                    {hotel.cnb_toggle && hotel.cnb_count > 0 && (
                                      <div>
                                        <p className="text-gray-500">CNB</p>
                                        <p className="font-medium text-gray-800">{hotel.cnb_count}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 italic pl-1">No hotels configured in this option</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : hotels && hotels.length > 0 ? (
                  <div className="space-y-4">
                    {hotels.map((hotel, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-gray-800 text-lg">{hotel.hotel_name || hotel.name}</p>
                            <p className="text-sm text-gray-500">{hotel.city || hotel.destination}</p>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700">
                            {getHotelStarLabel(hotel.category || hotel.hotel_category || quote.hotel_category)}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-gray-500">No of Rooms</p>
                            <p className="font-medium text-gray-800">{hotel.rooms_count || hotel.rooms || quote.no_of_rooms || 1}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Room Category</p>
                            <p className="font-medium text-gray-800">{hotel.room_type || quote.room_type || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Meal Plan</p>
                            <p className="font-medium text-gray-800">{(hotel.meal_plan || quote.meal_plan || '-').toUpperCase()}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Nights</p>
                            <p className="font-medium text-gray-800">{hotel.nights || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Check In</p>
                            <p className="font-medium text-gray-800">{formatDate(hotel.check_in)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Check Out</p>
                            <p className="font-medium text-gray-800">{formatDate(hotel.check_out)}</p>
                          </div>
                          {hotel.extra_bed_toggle && hotel.extra_bed_count > 0 && (
                            <div>
                              <p className="text-gray-500">EB / EM</p>
                              <p className="font-medium text-gray-800">{hotel.extra_bed_count}</p>
                            </div>
                          )}
                          {hotel.cwb_toggle && hotel.cwb_count > 0 && (
                            <div>
                              <p className="text-gray-500">CWB</p>
                              <p className="font-medium text-gray-800">{hotel.cwb_count}</p>
                            </div>
                          )}
                          {hotel.cnb_toggle && hotel.cnb_count > 0 && (
                            <div>
                              <p className="text-gray-500">CNB</p>
                              <p className="font-medium text-gray-800">{hotel.cnb_count}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : quote.hotel_name ? (
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold text-gray-800 text-lg">{quote.hotel_name}</p>
                        <p className="text-sm text-gray-500">{quote.destination}</p>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700">
                        {getHotelStarLabel(quote.hotel_category)}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">No of Rooms</p>
                        <p className="font-medium text-gray-800">{quote.no_of_rooms || 1}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Room Category</p>
                        <p className="font-medium text-gray-800">{quote.room_type || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Check In</p>
                        <p className="font-medium text-gray-800">{formatDate(quote.hotel_check_in || quote.travel_date)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Check Out</p>
                        <p className="font-medium text-gray-800">{formatDate(quote.hotel_check_out || quote.return_date)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Meal Plan</p>
                        <p className="font-medium text-gray-800">{quote.meal_plan || '-'}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No hotel details available</p>
                )}
              </CardContent>
              )}
            </Card>

            {/* Day-wise Itinerary */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ListOrdered className="w-5 h-5 text-indigo-600" />
                  Day-wise Itinerary
                </CardTitle>
              </CardHeader>
              <CardContent>
                {itinerary && itinerary.length > 0 ? (
                  <div className="space-y-0">
                    {itinerary.map((day, idx) => {
                      // Format date using helper
                      const dayDate = parseDayDate(day.date, quote.travel_date, idx);
                      const dateLabel = formatDayDateLabel(dayDate);
                      
                      // Get vehicle info
                      const vehicleName = day.vehicle || '';
                      const rawXfer = (day.transfer_type || '').toLowerCase();
                      const isNoTransfer = rawXfer === 'none' || rawXfer === 'no_transfer' || rawXfer === 'no-transfer';
                      const transferLabel = isNoTransfer ? ''
                        : rawXfer === 'sic' || rawXfer === 'seat_in_coach' || rawXfer === 'seat-in-coach' ? 'Seat in Coach (SIC)'
                        : rawXfer ? 'Private'
                        : '';
                      
                      return (
                        <div key={idx} className="relative" data-testid={`itinerary-day-${day.day || idx + 1}`}>
                          <div className="bg-gray-50/80 rounded-xl p-5 border border-gray-100 mb-3">
                              {/* Day & Date - City Name header */}
                              <div className="mb-3">
                                <h4 className="text-base font-bold text-gray-900 tracking-wide uppercase" data-testid={`day-${idx+1}-header`}>
                                  DAY {day.day || idx + 1}: {dateLabel}{(day.city || day.title) && idx !== itinerary.length - 1 ? ` - ${(day.city || day.title).toUpperCase()}` : ''}
                                </h4>
                              </div>
                              
                              {/* Activities - direct headline + description, no bullets */}
                              {day.activities && Array.isArray(day.activities) && day.activities.length > 0 && (
                                <div className="space-y-3 mb-4">
                                  {day.activities.map((activity, aIdx) => {
                                    const actName = typeof activity === 'string' ? activity : (activity.title || activity.name);
                                    const actDesc = typeof activity === 'object' ? activity.description : null;
                                    return (
                                      <div key={aIdx}>
                                        <p className="text-sm font-semibold text-gray-800">{actName}</p>
                                        {actDesc && <p className="text-sm text-gray-500 mt-0.5">{actDesc}</p>}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Description (if no activities) */}
                              {day.description && (!day.activities || day.activities.length === 0) && (
                                <p className="text-sm text-gray-600 leading-relaxed mb-4 whitespace-pre-line" data-testid={`day-${idx+1}-description`}>
                                  {day.description}
                                </p>
                              )}
                              
                              {/* Accommodation & Transfer bar */}
                              <div className="flex flex-wrap gap-4 pt-3 border-t border-gray-200">
                                {day.hotel && !hasMultipleOptions && (
                                  <div className="flex items-center gap-2 text-sm" data-testid={`day-${idx+1}-overnight`}>
                                    <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
                                      <Hotel className="w-4 h-4 text-indigo-600" />
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-400 block leading-none">Accommodation</span>
                                      <span className="text-gray-800 font-semibold text-sm">{day.hotel}</span>
                                    </div>
                                  </div>
                                )}
                                {(transferLabel || vehicleName) && (
                                  <div className="flex items-center gap-2 text-sm" data-testid={`day-${idx+1}-transfer`}>
                                    <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                                      <Car className="w-4 h-4 text-amber-600" />
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-400 block leading-none">Transfer</span>
                                      <span className="text-gray-800 font-semibold text-sm">
                                        {transferLabel}{vehicleName ? ` - ${vehicleName}` : ''}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : typeof quote.itinerary === 'string' && quote.itinerary ? (
                  <div className="text-sm text-gray-600 whitespace-pre-line bg-gray-50 p-4 rounded-lg">
                    {quote.itinerary}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <ListOrdered className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No itinerary details available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Inclusions */}
              <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-emerald-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-emerald-600">
                    <CheckCircle className="w-5 h-5" />
                    Inclusions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {quote.inclusions ? (
                    <ul className="space-y-2">
                      {quote.inclusions.split('\n').filter(line => line.trim()).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item.replace(/^[-•*]\s*/, '')}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">No inclusions specified</p>
                  )}
                </CardContent>
              </Card>

              {/* Exclusions */}
              <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-red-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-red-600">
                    <XCircle className="w-5 h-5" />
                    Exclusions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {quote.exclusions ? (
                    <ul className="space-y-2">
                      {quote.exclusions.split('\n').filter(line => line.trim()).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item.replace(/^[-•*]\s*/, '')}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">No exclusions specified</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Terms & Conditions */}
            {quote.show_terms_conditions && quote.terms_conditions && (
              <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-indigo-500" data-testid="terms-conditions-section">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-indigo-600">
                    <ScrollText className="w-5 h-5" />
                    Terms & Conditions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {quote.terms_conditions.split('\n').filter(line => line.trim()).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-indigo-400 mt-0.5 flex-shrink-0">&#8226;</span>
                        <span className="text-gray-700">{item.replace(/^[-•*]\s*/, '')}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Multi-Option DMC Price Cards — only when quote has 2+ accommodation options */}
            {hasMultipleOptions && accommodationOptions.map((opt, idx) => {
              const dmcPerAdult = opt.quote_per_adult || 0;
              const dmcPerCwb = opt.quote_per_cwb || 0;
              const dmcPerCnb = opt.quote_per_cnb || 0;
              const dmcTotal = opt.total_amount || 0;
              const dmcTotalGst = Math.round(dmcTotal * 1.05);
              // Apply shared markup (same agentMarkup used across all options)
              const finalPerAdult = dmcPerAdult + (markupMode === 'percentage' ? dmcPerAdult * (markupPercentage / 100) : (agentMarkup.adult || 0));
              const finalPerCwb = dmcPerCwb + (markupMode === 'percentage' ? dmcPerCwb * (markupPercentage / 100) : (agentMarkup.cwb || 0));
              const finalPerCnb = dmcPerCnb + (markupMode === 'percentage' ? dmcPerCnb * (markupPercentage / 100) : (agentMarkup.cnb || 0));
              const finalTotal = (finalPerAdult * (quote.pax_adults || 0)) + (finalPerCwb * (quote.cwb || 0)) + (finalPerCnb * (quote.cnb || 0));
              const finalTotalGst = Math.round(finalTotal * 1.05);
              const badgeColor = idx === 0 ? 'bg-blue-100 text-blue-700' : idx === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
              return (
                <Card key={`opt-${idx}`} className="bg-white border-gray-200 shadow-sm overflow-hidden" data-testid={`sidebar-option-card-${idx}`}>
                  <CardContent className="p-0">
                    {/* DMC Price block */}
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-5 h-5 text-emerald-600" />
                          <div className="font-semibold text-gray-900 text-base">DMC Price</div>
                        </div>
                        <span className={`px-2 py-0.5 ${badgeColor} text-xs rounded-full font-bold tracking-wider`}>
                          OPTION {idx + 1}
                        </span>
                      </div>
                      <div className="space-y-1.5 text-sm">
                        {dmcPerAdult > 0 && (
                          <div className="flex justify-between"><span className="text-gray-500">Per Adult</span><span className="font-mono font-semibold text-gray-800">{formatCurrency(dmcPerAdult)}</span></div>
                        )}
                        {dmcPerCwb > 0 && (
                          <div className="flex justify-between"><span className="text-gray-500">Per CWB</span><span className="font-mono font-semibold text-blue-600">{formatCurrency(dmcPerCwb)}</span></div>
                        )}
                        {dmcPerCnb > 0 && (
                          <div className="flex justify-between"><span className="text-gray-500">Per CNB</span><span className="font-mono font-semibold text-amber-700">{formatCurrency(dmcPerCnb)}</span></div>
                        )}
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center">
                        <span className="text-sm text-gray-700 font-medium">Total</span>
                        <span className="text-lg font-bold text-emerald-600 font-mono">{formatCurrency(dmcTotal)}</span>
                      </div>
                      {gstRequired && (
                        <div className="bg-emerald-50 -mx-4 mt-2 px-4 py-2 flex justify-between items-center">
                          <span className="text-sm text-emerald-800 font-semibold">Total with GST (5%)</span>
                          <span className="text-base font-bold text-emerald-700 font-mono">{formatCurrency(dmcTotalGst)}</span>
                        </div>
                      )}
                    </div>
                    {/* Your Final Price block (DMC + shared markup) */}
                    <div className="p-4 bg-gradient-to-r from-gray-50 to-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-base font-semibold text-gray-700">Your Final Price</div>
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-bold">OPTION {idx + 1} + MARKUP</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        {dmcPerAdult > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Per Adult</span>
                            <span className="font-mono text-gray-900">{formatCurrency(finalPerAdult)}</span>
                          </div>
                        )}
                        {dmcPerCwb > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Per CWB</span>
                            <span className="font-mono text-blue-600">{formatCurrency(finalPerCwb)}</span>
                          </div>
                        )}
                        {dmcPerCnb > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Per CNB</span>
                            <span className="font-mono text-amber-700">{formatCurrency(finalPerCnb)}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between items-center">
                        <span className="text-sm text-gray-700 font-medium">Total</span>
                        <span className="text-lg font-bold text-gray-900 font-mono">{formatCurrency(finalTotal)}</span>
                      </div>
                      {gstRequired && (
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-sm text-gray-600">Total with GST</span>
                          <span className="text-base font-bold text-amber-700 font-mono">{formatCurrency(finalTotalGst)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* DMC Price (single card — only for single-option quotes) */}
            {!hasMultipleOptions && (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  DMC Price {hasMultipleOptions && <Badge className="bg-emerald-100 text-emerald-700 text-xs ml-1">{getActiveOption().label || `Option ${selectedOption + 1}`}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(hasMultipleOptions ? getActiveOption().quote_per_adult : quote.quote_per_adult) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Per Adult</span>
                    <span className="font-medium text-gray-800">{formatCurrency(hasMultipleOptions ? getActiveOption().quote_per_adult : quote.quote_per_adult)}</span>
                  </div>
                )}
                {(hasMultipleOptions ? getActiveOption().quote_per_cwb : quote.quote_per_cwb) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Per CWB</span>
                    <span className="font-medium text-blue-600">{formatCurrency(hasMultipleOptions ? getActiveOption().quote_per_cwb : quote.quote_per_cwb)}</span>
                  </div>
                )}
                {(hasMultipleOptions ? getActiveOption().quote_per_cnb : quote.quote_per_cnb) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Per CNB</span>
                    <span className="font-medium text-amber-600">{formatCurrency(hasMultipleOptions ? getActiveOption().quote_per_cnb : quote.quote_per_cnb)}</span>
                  </div>
                )}
                {!hasMultipleOptions && quote.quote_per_child > 0 && !quote.quote_per_cwb && !quote.quote_per_cnb && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Per Child</span>
                    <span className="font-medium text-gray-800">{formatCurrency(quote.quote_per_child)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                  <span className="text-gray-700 font-medium">Total Amount</span>
                  <span className="text-xl font-bold text-emerald-600">
                    {formatCurrency(hasMultipleOptions ? getActiveOption().total_amount : quote.quote_amount)}
                  </span>
                </div>
                {gstRequired && (
                  <>
                    <div className="flex justify-between items-center text-sm pt-2">
                      <span className="text-gray-500">GST @ 5%</span>
                      <span className="font-medium text-gray-700">
                        {formatCurrency(Math.round((hasMultipleOptions ? getActiveOption().total_amount : quote.quote_amount) * 0.05))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-emerald-200 bg-emerald-50 -mx-6 px-6 pb-1 -mb-4 rounded-b-lg" data-testid="total-with-gst">
                      <span className="text-emerald-800 font-semibold">Total with GST</span>
                      <span className="text-xl font-bold text-emerald-700">
                        {formatCurrency(Math.round((hasMultipleOptions ? getActiveOption().total_amount : quote.quote_amount) * 1.05))}
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            )}

            {/* Add-on Costs: Flight / Hotel / Visa
                Agent fills these → totals roll up in the client PDF + show as inclusions.
                Single source of truth: persisted on the quote via /agent-update. */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="quote-addons-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Plus className="w-5 h-5 text-sky-600" />
                  Add-on Costs
                </CardTitle>
                <p className="text-xs text-gray-500 mt-1">Optional — appears as inclusions in the client PDF</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-gray-600 text-sm">Flight Price (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={addons.flight}
                    onChange={e => setAddons({ ...addons, flight: e.target.value })}
                    className="bg-white border-gray-200"
                    data-testid="quote-flight-price"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Hotel Price (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={addons.hotel}
                    onChange={e => setAddons({ ...addons, hotel: e.target.value })}
                    className="bg-white border-gray-200"
                    data-testid="quote-hotel-price"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Visa Price (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={addons.visa}
                    onChange={e => setAddons({ ...addons, visa: e.target.value })}
                    className="bg-white border-gray-200"
                    data-testid="quote-visa-price"
                    placeholder="0"
                  />
                </div>
                {((parseFloat(addons.flight) || 0) + (parseFloat(addons.hotel) || 0) + (parseFloat(addons.visa) || 0)) > 0 && (
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <span className="text-gray-700 text-sm font-medium">Add-on Total</span>
                    <span className="text-sky-700 font-bold">
                      {formatCurrency(
                        (parseFloat(addons.flight) || 0) +
                        (parseFloat(addons.hotel) || 0) +
                        (parseFloat(addons.visa) || 0)
                      )}
                    </span>
                  </div>
                )}
                <Button
                  onClick={handleSaveAddons}
                  disabled={savingAddons}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white mt-1"
                  data-testid="save-addons-btn"
                >
                  {savingAddons ? "Saving..." : "Save Add-ons"}
                </Button>
              </CardContent>
            </Card>

            {/* GST Billing Status */}
            {/* GST Billing Toggle */}
            <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2" data-testid="sidebar-gst-status">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">GST Billing</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={gstRequired}
                  onClick={async () => {
                    const newVal = !gstRequired;
                    setGstRequired(newVal);
                    try {
                      await axios.put(`${API}/quotes/${id}/agent-update`, { gst_required: newVal, gstin: newVal ? gstinValue : '' }, { headers: { Authorization: `Bearer ${token}` } });
                    } catch {}
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${gstRequired ? 'bg-emerald-600' : 'bg-gray-300'}`}
                  data-testid="gst-toggle"
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${gstRequired ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
              {gstRequired && (
                <Input
                  value={gstinValue}
                  onChange={(e) => setGstinValue(e.target.value.toUpperCase())}
                  onBlur={async () => {
                    try {
                      await axios.put(`${API}/quotes/${id}/agent-update`, { gstin: gstinValue }, { headers: { Authorization: `Bearer ${token}` } });
                    } catch {}
                  }}
                  placeholder="Enter GSTIN"
                  className="h-8 text-sm"
                  data-testid="gstin-input"
                />
              )}
            </div>

            {/* Agent Markup Section */}
            {(quote.quote_per_adult > 0 || quote.quote_amount > 0) && (
              <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 shadow-sm" data-testid="agent-markup-section">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg text-purple-800">
                      <Calculator className="w-5 h-5" />
                      My Markup
                    </CardTitle>
                    {/* Markup Mode Toggle */}
                    <div className="flex items-center gap-1 bg-white/70 rounded-lg p-1" data-testid="markup-mode-toggle">
                      <button
                        onClick={() => setMarkupMode("manual")}
                        data-testid="markup-manual-btn"
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                          markupMode === "manual" 
                            ? "bg-purple-600 text-white shadow-sm" 
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        Manual
                      </button>
                      <button
                        onClick={() => setMarkupMode("percentage")}
                        data-testid="markup-percentage-btn"
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                          markupMode === "percentage" 
                            ? "bg-purple-600 text-white shadow-sm" 
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        <Percent className="w-3 h-3" /> %
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {markupMode === "percentage" ? (
                    <div className="flex items-center gap-3">
                      <Label className="text-gray-600 whitespace-nowrap text-sm">Markup %</Label>
                      <Input 
                        type="number" 
                        value={markupPercentage || ""} 
                        onChange={e => setMarkupPercentage(parseFloat(e.target.value) || 0)}
                        className="w-24 bg-white border-purple-200"
                        placeholder="0"
                        data-testid="markup-percentage-input"
                      />
                      <span className="text-gray-500 text-sm">%</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-gray-600 text-sm">Per Adult</Label>
                        <Input 
                          type="number" 
                          value={agentMarkup.adult || ""} 
                          onChange={e => setAgentMarkup({...agentMarkup, adult: parseFloat(e.target.value) || 0})}
                          className="bg-white border-gray-200 mt-1"
                          placeholder="0"
                          data-testid="markup-adult-input"
                        />
                      </div>
                      {(quote.quote_per_cwb > 0 || quote.cwb_count > 0 || quote.cwb > 0) && (
                        <div>
                          <Label className="text-blue-600 text-sm">Per CWB</Label>
                          <Input 
                            type="number" 
                            value={agentMarkup.cwb || ""} 
                            onChange={e => setAgentMarkup({...agentMarkup, cwb: parseFloat(e.target.value) || 0})}
                            className="bg-blue-50 border-blue-200 mt-1"
                            placeholder="0"
                            data-testid="markup-cwb-input"
                          />
                        </div>
                      )}
                      {(quote.quote_per_cnb > 0 || quote.cnb_count > 0 || quote.cnb > 0) && (
                        <div>
                          <Label className="text-amber-600 text-sm">Per CNB</Label>
                          <Input 
                            type="number" 
                            value={agentMarkup.cnb || ""} 
                            onChange={e => setAgentMarkup({...agentMarkup, cnb: parseFloat(e.target.value) || 0})}
                            className="bg-amber-50 border-amber-200 mt-1"
                            placeholder="0"
                            data-testid="markup-cnb-input"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selling Price Breakdown */}
                  <div className="pt-3 border-t border-purple-200">
                    <p className="text-xs font-semibold text-purple-700 mb-2 uppercase tracking-wide">Selling Price</p>
                    <div className="bg-white/80 rounded-lg p-3 space-y-2">
                      {/* Adult selling price */}
                      <div className="flex justify-between text-sm" data-testid="selling-price-adult">
                        <span className="text-gray-600">Per Adult</span>
                        <span className="font-medium text-gray-800">
                          {formatCurrency(
                            (quote.quote_per_adult || 0) + 
                            (markupMode === "percentage" 
                              ? (quote.quote_per_adult || 0) * (markupPercentage / 100)
                              : agentMarkup.adult)
                          )}
                        </span>
                      </div>
                      
                      {/* CWB selling price */}
                      {(quote.quote_per_cwb > 0 || quote.cwb_count > 0 || quote.cwb > 0) && (
                        <div className="flex justify-between text-sm" data-testid="selling-price-cwb">
                          <span className="text-blue-600">Per CWB</span>
                          <span className="font-medium text-blue-600">
                            {formatCurrency(
                              (quote.quote_per_cwb || 0) + 
                              (markupMode === "percentage" 
                                ? (quote.quote_per_cwb || 0) * (markupPercentage / 100)
                                : agentMarkup.cwb)
                            )}
                          </span>
                        </div>
                      )}

                      {/* CNB selling price */}
                      {(quote.quote_per_cnb > 0 || quote.cnb_count > 0 || quote.cnb > 0) && (
                        <div className="flex justify-between text-sm" data-testid="selling-price-cnb">
                          <span className="text-amber-600">Per CNB</span>
                          <span className="font-medium text-amber-600">
                            {formatCurrency(
                              (quote.quote_per_cnb || 0) + 
                              (markupMode === "percentage" 
                                ? (quote.quote_per_cnb || 0) * (markupPercentage / 100)
                                : agentMarkup.cnb)
                            )}
                          </span>
                        </div>
                      )}

                      {/* Grand Total */}
                      <div className="flex justify-between items-center pt-3 border-t border-gray-200" data-testid="selling-grand-total">
                        <span className="text-sm font-semibold text-purple-800">Grand Total</span>
                        <span className="text-xl font-bold text-purple-600">
                          {formatCurrency(
                            (() => {
                              const adultRate = (quote.quote_per_adult || 0) + 
                                (markupMode === "percentage" 
                                  ? (quote.quote_per_adult || 0) * (markupPercentage / 100)
                                  : agentMarkup.adult);
                              const cwbRate = (quote.quote_per_cwb || 0) + 
                                (markupMode === "percentage" 
                                  ? (quote.quote_per_cwb || 0) * (markupPercentage / 100)
                                  : agentMarkup.cwb);
                              const cnbRate = (quote.quote_per_cnb || 0) + 
                                (markupMode === "percentage" 
                                  ? (quote.quote_per_cnb || 0) * (markupPercentage / 100)
                                  : agentMarkup.cnb);
                              
                              return (adultRate * (quote.pax_adults || 0)) + 
                                     (cwbRate * (quote.cwb_count || quote.cwb || 0)) + 
                                     (cnbRate * (quote.cnb_count || quote.cnb || 0));
                            })()
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Save Markup Button */}
                  <Button 
                    onClick={handleSaveMarkup} 
                    disabled={savingMarkup}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="save-markup-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingMarkup ? "Saving..." : "Save Markup"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* DMC User Details */}
            {(quote.assigned_to_name || quote.created_by_name) && (
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="w-5 h-5 text-blue-600" />
                    DMC User Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">User Name</p>
                      <p className="font-semibold text-gray-800">
                        {quote.assigned_to_name || quote.created_by_name || 'Admin'}
                      </p>
                    </div>
                  </div>
                  {quote.assigned_user_designation && (
                    <div className="flex items-start gap-3">
                      <Briefcase className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Designation</p>
                        <p className="text-gray-800">{quote.assigned_user_designation}</p>
                      </div>
                    </div>
                  )}
                  {quote.assigned_user_phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Mobile Number</p>
                        <a href={`tel:${quote.assigned_user_phone}`} className="text-emerald-600 font-medium hover:underline">
                          {quote.assigned_user_phone}
                        </a>
                      </div>
                    </div>
                  )}
                  {quote.assigned_user_email && (
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <a href={`mailto:${quote.assigned_user_email}`} className="text-blue-600 hover:underline text-sm break-all">
                          {quote.assigned_user_email}
                        </a>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="timeline-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-gray-600" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-sky-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Quotation Requested</p>
                    <p className="text-sm text-gray-500">
                      {quote.created_at ? (() => { const d = new Date(quote.created_at); const dd = String(d.getDate()).padStart(2,'0'); const mm = String(d.getMonth()+1).padStart(2,'0'); const yr = d.getFullYear(); const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }); return `${dd}/${mm}/${yr} at ${time}`; })() : '-'}
                    </p>
                  </div>
                </div>
                {quote.updated_at && quote.status !== 'pending' && (
                  <div className="flex items-start gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Quotation Received</p>
                      <p className="text-sm text-gray-500">
                        {(() => { const d = new Date(quote.updated_at); const dd = String(d.getDate()).padStart(2,'0'); const mm = String(d.getMonth()+1).padStart(2,'0'); const yr = d.getFullYear(); const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }); return `${dd}/${mm}/${yr} at ${time}`; })()}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-4 space-y-2">
                <Button 
                  className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 text-white" 
                  onClick={() => navigate(`/agent/chat?to=${quote.created_by || ''}&quote_id=${quote.id}&quote_number=${quote.quote_number || ''}`)}
                  data-testid="sidebar-chat-admin-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Chat with Admin
                </Button>
                <Button 
                  className="w-full justify-start bg-green-600 hover:bg-green-700 text-white" 
                  onClick={shareViaWhatsApp}
                  disabled={sharingWhatsApp}
                  data-testid="sidebar-share-whatsapp-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> {sharingWhatsApp ? 'Generating PDF...' : 'Share PDF on WhatsApp'}
                </Button>
                <Button 
                  variant="outline"
                  className="w-full justify-start border-blue-300 text-blue-600 hover:bg-blue-50" 
                  onClick={() => {
                    const email = quote.client_email || '';
                    const subject = `Travel Quotation - ${quote.destination || 'Your Trip'} (${nights} Nights)`;
                    const body = `Dear ${quote.client_name || 'Guest'},\n\nPlease find attached your travel quotation for ${quote.destination || 'your trip'}.\n\nBest Regards,\n${user?.company_name || user?.name || 'Travel Agent'}`;
                    window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                  }}
                  data-testid="sidebar-share-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" /> Share with Email
                </Button>
                <Button 
                  variant="outline"
                  className="w-full justify-start border-sky-300 text-sky-600 hover:bg-sky-50" 
                  onClick={downloadPDF}
                  data-testid="sidebar-download-pdf-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button 
                  variant="outline"
                  className="w-full justify-start border-purple-300 text-purple-600 hover:bg-purple-50" 
                  onClick={downloadWord}
                  data-testid="sidebar-download-word-btn"
                >
                  <FileType className="w-4 h-4 mr-2" /> Download Word
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Revision History */}
      {quote.revision_history && quote.revision_history.length > 0 && (
        <Card className="bg-white border-gray-200 shadow-sm mt-6" data-testid="revision-history-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <RotateCcw className="w-5 h-5 text-orange-500" />
              Revision History
              <Badge className="bg-orange-100 text-orange-700 text-xs ml-2">
                {quote.revision_history.length} Revision{quote.revision_history.length > 1 ? 's' : ''}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[...quote.revision_history].reverse().map((rev, idx) => (
              <div key={idx} className={`rounded-lg p-3 border ${rev.status === 'pending' ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`} data-testid={`revision-item-${rev.revision_num}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-700">Revision #{rev.revision_num}</span>
                    {rev.status === 'pending' ? (
                      <Badge className="bg-orange-100 text-orange-600 text-[10px]">Pending</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-600 text-[10px]">Completed</Badge>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400">{formatDate(rev.request_date)}</span>
                </div>
                {rev.categories && rev.categories.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {rev.categories.map((cat, ci) => (
                      <span key={ci} className="text-[10px] bg-white border border-gray-200 rounded px-1.5 py-0.5 text-gray-600">{cat}</span>
                    ))}
                  </div>
                )}
                <p className="text-sm text-gray-700">{rev.notes}</p>
                {rev.completed_date && (
                  <p className="text-[10px] text-emerald-600 mt-1">Completed: {formatDate(rev.completed_date)}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Request Revision Modal */}
      {showConfirmOptionModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => !confirmingBooking && setShowConfirmOptionModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="confirm-option-modal">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Select Option to Confirm</h3>
                <p className="text-xs text-gray-500">Pick the hotel option your client wants to book. Admin will be notified.</p>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              {accommodationOptions.map((opt, idx) => {
                const dmcPerAdult = opt.quote_per_adult || 0;
                const dmcPerCwb = opt.quote_per_cwb || 0;
                const dmcPerCnb = opt.quote_per_cnb || 0;
                const dmcTotal = opt.total_amount || 0;
                const isSelected = confirmSelectedOption === idx;
                const badgeColor = idx === 0 ? 'bg-blue-100 text-blue-700' : idx === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
                const hotelNames = (opt.hotels || []).map(h => h.hotel_name || h.name).filter(Boolean).join(', ');
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setConfirmSelectedOption(idx)}
                    className={`w-full text-left bg-white rounded-lg p-4 border-2 transition-all ${
                      isSelected
                        ? 'border-emerald-500 ring-2 ring-emerald-200 shadow-md'
                        : 'border-gray-200 hover:border-emerald-300 hover:shadow-sm'
                    }`}
                    data-testid={`confirm-option-choice-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 ${badgeColor} text-[10px] rounded-full font-bold tracking-wider`}>OPTION {idx + 1}</span>
                        {hotelNames && <span className="text-sm font-semibold text-gray-800">{hotelNames}</span>}
                      </div>
                      {isSelected && (
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-xs mt-2">
                      {dmcPerAdult > 0 && (
                        <div>
                          <div className="text-gray-500">Per Adult</div>
                          <div className="font-mono font-semibold text-gray-800">{formatCurrency(dmcPerAdult)}</div>
                        </div>
                      )}
                      {dmcPerCwb > 0 && (
                        <div>
                          <div className="text-gray-500">Per CWB</div>
                          <div className="font-mono font-semibold text-blue-600">{formatCurrency(dmcPerCwb)}</div>
                        </div>
                      )}
                      {dmcPerCnb > 0 && (
                        <div>
                          <div className="text-gray-500">Per CNB</div>
                          <div className="font-mono font-semibold text-amber-700">{formatCurrency(dmcPerCnb)}</div>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-2 border-t border-gray-100 flex justify-between items-center">
                      <span className="text-xs text-gray-600 font-medium">Total</span>
                      <span className="text-base font-bold text-emerald-700 font-mono">{formatCurrency(dmcTotal)}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <Button variant="outline" onClick={() => setShowConfirmOptionModal(false)} disabled={confirmingBooking}>Cancel</Button>
              {(() => {
                const hasGuest = !!((quote?.client_name || guestForm.client_name)?.trim() && (quote?.client_phone || guestForm.client_phone)?.trim());
                return (
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={confirmBookingWithOption}
                    disabled={confirmingBooking || confirmSelectedOption === null || !hasGuest}
                    title={!hasGuest ? "Fill Guest Name and Mobile to enable confirmation" : ""}
                    data-testid="submit-confirm-option-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {confirmingBooking ? 'Confirming...' : !hasGuest ? 'Add Guest Info First' : (confirmSelectedOption !== null ? `Confirm Option ${confirmSelectedOption + 1}` : 'Select an Option')}
                  </Button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {showRevisionModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowRevisionModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="revision-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <RotateCcw className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Request Revision</h3>
                <p className="text-xs text-gray-500">Tell admin what changes you need</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">What needs to change?</label>
                <div className="flex flex-wrap gap-2">
                  {["Hotel Preference", "Budget Change", "Itinerary Change", "Add/Remove Activities", "Dates Change", "Pax Change", "Other"].map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setRevisionCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat])}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${revisionCategories.includes(cat) ? 'bg-orange-100 border-orange-400 text-orange-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                      data-testid={`revision-cat-${cat.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Revision Notes <span className="text-red-500">*</span></label>
                <textarea
                  value={revisionNotes}
                  onChange={e => setRevisionNotes(e.target.value)}
                  rows={4}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                  placeholder="E.g., Need a cheaper hotel option, add one more day in Leh, change travel dates to 15th June..."
                  data-testid="revision-notes-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-5">
              <Button variant="outline" onClick={() => setShowRevisionModal(false)}>Cancel</Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleRequestRevision}
                disabled={requestingRevision || !revisionNotes.trim()}
                data-testid="submit-revision-btn"
              >
                <Send className="w-4 h-4 mr-2" />{requestingRevision ? 'Sending...' : 'Send Revision Request'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {showThankYou && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          data-testid="thank-you-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden"
            style={{ animation: "scaleIn 0.4s cubic-bezier(0.175,0.885,0.32,1.275)" }}
          >
            {/* Green gradient header */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 px-8 py-8 text-center relative overflow-hidden">
              {/* decorative circles */}
              <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-white/10" />
              <div className="absolute -bottom-8 -right-8 w-36 h-36 rounded-full bg-white/10" />
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-9 h-9 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-1 leading-tight">
                  Thank You for Your
                  <br />Valuable Booking!
                </h2>
                <p className="text-emerald-100 text-sm mt-1">Booking Confirmed Successfully</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-8 py-6 space-y-4">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 space-y-2">
                {quote?.quote_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Quote Number</span>
                    <span className="font-semibold text-gray-800">{quote.quote_number}</span>
                  </div>
                )}
                {quote?.destination && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Destination</span>
                    <span className="font-semibold text-gray-800">{quote.destination}</span>
                  </div>
                )}
                {quote?.client_name && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Guest Name</span>
                    <span className="font-semibold text-gray-800">{quote.client_name}</span>
                  </div>
                )}
                {quote?.travel_date && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Travel Date</span>
                    <span className="font-semibold text-gray-800">{formatDate(quote.travel_date)}</span>
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-500 text-center leading-relaxed">
                Our team has been notified and will start processing your booking right away.
                You can track the status from your dashboard anytime.
              </p>

              <button
                data-testid="close-thank-you-btn"
                onClick={() => setShowThankYou(false)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.8); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

    </DashboardLayout>
  );
};

export default QuoteDetail;
